// The MAP mode of the Practise view: the tune map (music/tuneMap.ts) drawn on
// the chart's own bar grid, so the playhead, the loop, the section marks and
// the transport work exactly as they do on the chart. Each cell is a bracket
// over its bars carrying an interval chip, the device's tag and the shape;
// the chords sit under the bracket in light type as the check row. Three
// levels of detail: the chords shown (learning), faded and revealed on tap
// (testing), or no check row at all (brackets only); the quiz hides the
// chords whatever the level, since the chart has been transposed and the
// chords are the answer. An interval chip opens its fretboard hint and names
// the soloing key, or the landing an approach cell leans into.

import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import type { IRealChart, IRealMeasure } from '@/music/ireal/types';
import { DEVICE_LABELS, type TuneMap, type TuneMapCell } from '@/music/tuneMap';
import type { TuneMapDetail } from '@/state/store';
import { ChartGrid } from './ChartView';
import { ChordSymbol } from './chartChrome';
import { cellHint } from './tuneMapHint';

interface Segment {
  cell: TuneMapCell | null;
  /** Beats of the bar the segment covers. */
  grow: number;
  edge: 'only' | 'start' | 'mid' | 'end' | 'gap';
  /** The segment that carries the cell's label. */
  label: boolean;
}

/** A label wants two beats of room: the first bar of the cell that gives it
 *  that carries the label, else the cell's first bar (a half-beat anticipation
 *  opening a cadence must not carry the whole label into the bar before). */
function labelBars(map: TuneMap): Map<string, number> {
  const out = new Map<string, number>();
  for (const cell of map.cells) {
    let chosen = cell.bars[0];
    for (let bar = cell.bars[0]; bar <= cell.bars[1]; bar++) {
      const left = Math.max(cell.start, map.barStarts[bar]);
      const right = Math.min(cell.end, map.barEnds[bar]);
      if (right - left >= Math.min(2, cell.end - cell.start) - 1e-9) {
        chosen = bar;
        break;
      }
    }
    out.set(cell.id, chosen);
  }
  return out;
}

function barSegments(map: TuneMap, bar: number, labels: Map<string, number>): Segment[] {
  const start = map.barStarts[bar];
  const end = map.barEnds[bar];
  const segments: Segment[] = [];
  let cursor = start;
  for (const cell of map.cells) {
    if (cell.end <= start + 1e-9 || cell.start >= end - 1e-9) continue;
    const left = Math.max(cell.start, start);
    const right = Math.min(cell.end, end);
    if (left > cursor + 1e-9)
      segments.push({ cell: null, grow: left - cursor, edge: 'gap', label: false });
    const starts = cell.start >= start - 1e-9;
    const ends = cell.end <= end + 1e-9;
    segments.push({
      cell,
      grow: right - left,
      edge: starts && ends ? 'only' : starts ? 'start' : ends ? 'end' : 'mid',
      label: labels.get(cell.id) === bar,
    });
    cursor = right;
  }
  if (cursor < end - 1e-9)
    segments.push({ cell: null, grow: end - cursor, edge: 'gap', label: false });
  return segments;
}

interface TuneMapViewProps {
  chart: IRealChart;
  map: TuneMap;
  detail: TuneMapDetail;
  /** The chart is transposed and the chords are the answer: hidden until tapped. */
  cellsPerRow?: number;
  rowWindow?: readonly [number, number];
  playingMeasureId?: string | null;
  passageMeasureIds?: ReadonlySet<string>;
  onSelectPassage?: (fromId: string, toId: string, opts?: { extend?: boolean }) => void;
  onPassageGesture?: () => void;
  onTapMeasure?: (id: string) => void;
  cursorMeasureId?: string | null;
}

export function TuneMapView({
  chart,
  map,
  detail,
  cellsPerRow,
  rowWindow,
  playingMeasureId,
  passageMeasureIds,
  onSelectPassage,
  onPassageGesture,
  onTapMeasure,
  cursorMeasureId,
}: TuneMapViewProps) {
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(() => new Set());
  const [openHint, setOpenHint] = useState<string | null>(null);
  // A new map (another tune, a different level) starts
  // with every chord hidden again and no hint open.
  useEffect(() => {
    setRevealed(new Set());
    setOpenHint(null);
  }, [map, detail]);
  const labels = useMemo(() => labelBars(map), [map]);
  const barIndex = useMemo(
    () => new Map(chart.measures.map((m, i) => [m.id, i] as const)),
    [chart],
  );
  // Which cell each written chord belongs to, so a bar's check row fades and
  // reveals chord by chord with the cells.
  const cellOfChord = useMemo(() => {
    const out = new Map<string, TuneMapCell>();
    for (const cell of map.cells) for (const chord of cell.chords) out.set(chord.id, cell);
    return out;
  }, [map]);
  // Dimmed and hidden chords wait for a tap on their bar. Hidden chords keep
  // their row and simply vanish.
  const interactive = detail !== 'shown';
  const blank = detail === 'hidden';

  const toggleReveal = (bar: number) => {
    const touching = map.cells.filter((c) => c.bars[0] <= bar && c.bars[1] >= bar);
    setRevealed((old) => {
      const next = new Set(old);
      const all = touching.every((c) => next.has(c.id));
      for (const c of touching) {
        if (all) next.delete(c.id);
        else next.add(c.id);
      }
      return next;
    });
  };

  const renderLabel = (cell: TuneMapCell) => {
    const open = openHint === cell.id;
    const hint = cellHint(map, cell);
    return (
      <span className="map-label">
        <button
          type="button"
          className="map-chip"
          title={hint}
          aria-label={hint}
          aria-expanded={open}
          data-testid="map-chip"
          onClick={(e) => {
            e.stopPropagation();
            setOpenHint(open ? null : cell.id);
          }}
        >
          {cell.interval}
        </button>
        {cell.name && <span className="map-tag">{DEVICE_LABELS[cell.name]}</span>}
        <span className="map-shape">{cell.shape}</span>
        {cell.pedal && <span className="map-tag">pedal {cell.pedal.degree}</span>}
        {open && (
          <span className="map-hint" role="note" data-testid="map-hint">
            {hint}
          </span>
        )}
      </span>
    );
  };

  const renderBody = (m: IRealMeasure) => {
    const bar = barIndex.get(m.id) ?? 0;
    const segments = barSegments(map, bar, labels);
    const chords = m.chords.map((c) => {
      const cell = cellOfChord.get(c.id);
      const faded = interactive && !!cell && !revealed.has(cell.id);
      return (
        <span
          key={c.id}
          className={`map-chord${faded ? ' hidden' : ''}${faded && blank ? ' blank' : ''}`}
          style={{ '--grow': Math.max(0.25, c.beats || 1) } as CSSProperties}
          data-testid="map-chord"
        >
          {c.noChord ? 'N.C.' : <ChordSymbol symbol={c.symbol} className="map-chord-symbol" />}
        </span>
      );
    });
    return (
      <div className="map-bar">
        <div className="map-lane">
          {segments.map((seg, i) => (
            <span
              key={i}
              className={`map-seg${seg.cell ? ` cell-${seg.edge}` : ' cell-gap'}`}
              style={{ '--grow': Math.max(0.25, seg.grow) } as CSSProperties}
              data-cell={seg.cell?.id}
              data-approach={seg.cell ? String(seg.cell.approach) : undefined}
              data-testid={seg.cell && seg.label ? 'map-cell' : undefined}
            >
              {seg.cell && seg.label && renderLabel(seg.cell)}
            </span>
          ))}
        </div>
        {m.chords.length > 0 &&
          (interactive ? (
            <button
              type="button"
              className="map-chords map-reveal"
              aria-label="Reveal the chords"
              onClick={(e) => {
                e.stopPropagation();
                toggleReveal(bar);
              }}
            >
              {chords}
            </button>
          ) : (
            <span className="map-chords">{chords}</span>
          ))}
      </div>
    );
  };

  return (
    <div className="tune-map-sheet" data-detail={detail}>
      <ChartGrid
        chart={chart}
        cellsPerRow={cellsPerRow}
        rowWindow={rowWindow}
        gridClassName="chart-view lead-sheet tune-map"
        measureBaseClassName="chart-measure"
        ariaLabel="Tune map"
        passageMeasureIds={passageMeasureIds}
        playingMeasureId={playingMeasureId}
        onSelectPassage={onSelectPassage}
        onPassageGesture={onPassageGesture}
        onTapMeasure={onTapMeasure}
        cursorMeasureId={cursorMeasureId}
        renderBody={renderBody}
      />
    </div>
  );
}
